import { ApiError } from '../api/client.ts';
import { useSession } from '../auth/session.tsx';

/**
 * The server refused the ACCOUNT, not the request: /me answered 403 ("No staff
 * account is linked to this sign-in." or "This staff account is deactivated.").
 * Signing in again as the same account cannot help, so this says which account
 * it was and offers another (19 October 2026). That day the browser had moved
 * Procura to sys@ without anyone choosing it, and the refusal named nobody.
 */
export function isAccountRefusal(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Names the refused account (its email, from the browser's active account,
 * which is the account whose token was refused) and offers Microsoft's account
 * picker or signing out. The server's own words and hint are shown as sent.
 */
export function AccountRefused({ error }: { error: ApiError }) {
  const { account, signInWithDifferentAccount, signOut } = useSession();
  return (
    <div role="alert" className="mx-auto max-w-2xl p-6">
      <h2 className="text-lg font-bold text-neutral-900">Procura does not recognise this account</h2>
      <p className="mt-3 text-neutral-800">
        Signed in as{' '}
        {account
          ? <strong className="break-all font-semibold">{account.username}</strong>
          : 'this Microsoft account'}.
      </p>
      <p className="mt-2 text-neutral-700">{error.message}</p>
      {error.hint && <p className="mt-1 text-neutral-600">{error.hint}</p>}
      <p className="mt-1 text-neutral-600">
        If this is not the account you meant to use, sign in with a different one. Nothing was loaded.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void signInWithDifferentAccount()}
          className="min-h-[48px] rounded-lg bg-neutral-900 px-5 font-semibold text-white active:bg-neutral-700"
        >
          Sign in with a different account
        </button>
        {/* A reload after signing out, as the header's Sign out does. */}
        <button
          type="button"
          onClick={() => void signOut().then(() => location.assign('/'))}
          className="min-h-[48px] rounded-lg border border-neutral-400 px-5 font-semibold text-neutral-900"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
